import enviro from 'enviro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import UsageTracker from '../../../utils/UsageTracker';
import { MOCK_QUIZ_ME_DATA } from '../mockData';
import { AI_INTERACTIVE_EDUCATION_RELEASE_OTG_IDS } from '../../../constants/interactiveEducationConstants';
const QuizMeDefaultState = {
  LESSON: {},
  SHORT_FORM_CONTENT: {},
  TRACK: {}
};
const QuizMeDefaultCacheState = {
  LESSON: {},
  SHORT_FORM_CONTENT: {},
  TRACK: {}
};
export const isQuizMeReleaseShortId = shortId => enviro.isProd() && AI_INTERACTIVE_EDUCATION_RELEASE_OTG_IDS.includes(shortId);
const useQuizMeSessionConfig = create(set => Object.assign({}, QuizMeDefaultState, {
  deleteQuizState: ({
    contentType,
    contentId
  }) => {
    set(state => {
      const filteredState = Object.assign({}, state[contentType]);
      delete filteredState[contentId];
      return {
        [contentType]: filteredState
      };
    });
  },
  incrementQuestionWithAnswer: ({
    contentType,
    contentId,
    correct
  }) => {
    set(state => {
      const newState = {
        questionIndex: state[contentType][contentId].questionIndex + 1,
        correctAnswers: state[contentType][contentId].correctAnswers + (correct ? 1 : 0)
      };
      return {
        [contentType]: Object.assign({}, state[contentType], {
          [contentId]: newState
        })
      };
    });
  },
  initialiseQuiz: ({
    contentType,
    contentId
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [contentId]: {
          questionIndex: 0,
          correctAnswers: 0
        }
      })
    }));
  }
}));
const useQuizMeCacheConfig = create(set => Object.assign({}, QuizMeDefaultCacheState, {
  deleteQuizCacheState: ({
    contentType,
    contentId
  }) => {
    set(state => {
      const filteredState = Object.assign({}, state[contentType]);
      delete filteredState[contentId];
      return {
        [contentType]: filteredState
      };
    });
  },
  addQuizCacheState: ({
    contentType,
    contentId,
    quiz
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [contentId]: quiz
      })
    }));
  }
}));
export const useQuizMeSession = () => {
  const {
    LESSON,
    SHORT_FORM_CONTENT,
    TRACK,
    deleteQuizState,
    incrementQuestionWithAnswer,
    initialiseQuiz
  } = useQuizMeSessionConfig();
  const getQuizState = useCallback((id, contentType) => {
    switch (contentType) {
      case 'LESSON':
        return LESSON[id];
      case 'SHORT_FORM_CONTENT':
        return SHORT_FORM_CONTENT[id];
      case 'TRACK':
        return TRACK[id];
      default:
        return undefined;
    }
  }, [LESSON, SHORT_FORM_CONTENT, TRACK]);
  return {
    deleteQuizState,
    getQuizState,
    incrementQuestionWithAnswer,
    initialiseQuiz
  };
};

/**
 * Cache handler for mocked quiz data
 */
export const useMockedQuizMeQuery = ({
  contentType,
  contentId
}) => {
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const {
    addQuizCacheState,
    deleteQuizCacheState,
    LESSON,
    SHORT_FORM_CONTENT,
    TRACK
  } = useQuizMeCacheConfig();
  const {
    initialiseQuiz
  } = useQuizMeSession();
  const cachedQuizData = useMemo(() => {
    if (contentType === 'LESSON') {
      return LESSON[contentId];
    } else if (contentType === 'SHORT_FORM_CONTENT') {
      return SHORT_FORM_CONTENT[contentId];
    } else if (contentType === 'TRACK') {
      return TRACK[contentId];
    }
  }, [contentId, contentType, LESSON, SHORT_FORM_CONTENT, TRACK]);
  const fetchMockedQuizData = useCallback(() => {
    if (cachedQuizData) {
      setData(cachedQuizData);
      setLoading(false);
      return;
    }

    // Simulate API call with 1 second delay
    setLoading(true);
    setData(undefined);
    deleteQuizCacheState({
      contentType,
      contentId
    });
    setTimeout(() => {
      setData({
        quiz: MOCK_QUIZ_ME_DATA[contentId].quiz
      });
      setLoading(false);
      addQuizCacheState({
        contentType,
        contentId,
        quiz: {
          quiz: MOCK_QUIZ_ME_DATA[contentId].quiz
        }
      });
      initialiseQuiz({
        contentType,
        contentId
      });
      UsageTracker.track('interaction', {
        action: 'ai-quiz-me-generated-quiz',
        id: contentId,
        contentType,
        position: MOCK_QUIZ_ME_DATA[contentId].quiz.length
      });
    }, 1000);
  }, [cachedQuizData, addQuizCacheState, contentType, contentId, deleteQuizCacheState, initialiseQuiz]);
  const refetch = useCallback(() => {
    setLoading(true);
    setData(undefined);
    deleteQuizCacheState({
      contentType,
      contentId
    });
    fetchMockedQuizData();
  }, [contentId, contentType, deleteQuizCacheState, fetchMockedQuizData]);
  useEffect(() => {
    fetchMockedQuizData();
  }, [contentId, contentType, fetchMockedQuizData]);
  return {
    data,
    loading,
    refetch
  };
};